import { Injectable } from '@angular/core';
import {
  EmailListItemContract,
  TransformEmailListItemContract,
} from '@app-types/api/email';
import { EmailIcons } from '@app-types/enums/email-icons';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { EmailState } from '@app-types/enums/email-state';
import { MAIL_ACTIONS } from '@app-shared/constants/constants';
import { EmailActionsEnum } from '@app-types/enums/EmailActions';
import { EmailType } from '@app-types/enums/email-type';
import { FormatEmailIconsByFolderType } from '@app-pipes/format-email-icons-by-folder-type.pipe';
import { ViewTypeEnum } from '@app-types/enums/view-type';
import { ArchiveState } from '@app-types/enums/archiving';

@Injectable({ providedIn: 'root' })
export class TransformDataTableService {
  private formatEmailIconsByFolderType = new FormatEmailIconsByFolderType();

  constructor(private userWorkspaceService: UserWorkspaceService) {}

  public isRowWithError(email: EmailListItemContract): boolean {
    const emailState = email.state;
    const emailType = email.type;
    const emailStates = [
      EmailState.DeliveryError,
      EmailState.VirusDetected,
      EmailState.DispatchError,
      EmailState.NotDispatched,
      EmailState.NotAccepted,
      EmailState.SendError,
    ];

    const emailTypes = [EmailType.DeliveryError, EmailType.NonAcceptance];

    return emailStates.includes(emailState) || emailTypes.includes(emailType);
  }

  public folderName(mailFolderId: number, mailAccountId?: number): string {
    const emailFolder = this.userWorkspaceService.userFolders.find(
      e => e.mailFolderId === mailFolderId && e.mailAccountId === mailAccountId
    );
    return emailFolder
      ? emailFolder.creationType === 1
        ? 'folderType' + emailFolder.folderType
        : emailFolder.mailFolderName
      : '';
  }

  public isSentStates(emailState: number): boolean {
    const emailStates = [
      EmailState.Sent,
      EmailState.Delivered,
      EmailState.DeliveryError,
      EmailState.Sending,
      EmailState.Downloaded,
      EmailState.NotAccepted,
      EmailState.Accepted,
    ];
    return emailStates.includes(emailState);
  }

  getSentStatus(email: EmailListItemContract): number | undefined {
    const { state, sourceFolderType } = email;

    if (!this.isSentStates(state)) return;

    if (
      (state === EmailState.Sent || state === EmailState.Downloaded) &&
      sourceFolderType !== MailFolderType.Inbox
    ) {
      return EmailState.Sent;
    }

    return state;
  }

  getActionButton(
    email: EmailListItemContract,
    selectedMailFolderId: number | null,
    reassigned: boolean | null,
    canFinalDelete: boolean,
    mailAccFolderType?: MailFolderType,
    mailAccountId?: number
  ): string[] {
    const emailFolderType = this.userWorkspaceService?.userFolders?.find(
      e =>
        e.mailFolderId === email.mailFolderId &&
        e.mailAccountId === mailAccountId
    )?.folderType;

    return [...MAIL_ACTIONS].filter(el => {
      switch (el) {
        case EmailActionsEnum.Validate:
          return this.showIconById(EmailIcons.Validate, emailFolderType);
        case EmailActionsEnum.DeleteFinalMail:
          return (
            (mailAccFolderType === MailFolderType.Trash ||
              mailAccFolderType === MailFolderType.Spam) &&
            canFinalDelete
          );
        case EmailActionsEnum.Reply:
          return this.showIconById(EmailIcons.Reply, emailFolderType);
        case EmailActionsEnum.ReplyAll:
          return this.showIconById(EmailIcons.ReplyAll, emailFolderType);
        case EmailActionsEnum.Forward:
          return this.showIconById(EmailIcons.Forward, emailFolderType);
        case EmailActionsEnum.Reassign:
          return (
            this.showIconById(EmailIcons.Reassign, emailFolderType) &&
            !reassigned
          );
        case EmailActionsEnum.Groups:
          return this.showIconById(EmailIcons.Groups, emailFolderType);
        case EmailActionsEnum.Tags:
          return this.showIconById(EmailIcons.Tags, emailFolderType);
        case EmailActionsEnum.SetRead:
        case EmailActionsEnum.SetUnread:
          return this.showIconById(EmailIcons.Seen, emailFolderType);
        case EmailActionsEnum.MarkAsSpam:
          return this.showIconById(EmailIcons.Spam, emailFolderType);
        case EmailActionsEnum.Delete:
          return this.showIconById(EmailIcons.Delete, emailFolderType);
        case EmailActionsEnum.DownloadSingleEml:
          return this.showIconById(EmailIcons.DownloadEML, emailFolderType);
        case EmailActionsEnum.DownloadEml:
          return this.showIconById(EmailIcons.DownloadEML, emailFolderType);
        case EmailActionsEnum.Restore:
          return this.showIconById(EmailIcons.Restore, emailFolderType);
        case EmailActionsEnum.MarkAsNotSpam:
          return this.showIconById(EmailIcons.NotSpam, emailFolderType);
        case EmailActionsEnum.MoveToFolder:
          return this.showIconById(EmailIcons.Move, emailFolderType);
        case EmailActionsEnum.Archive:
          return this.showIconById(EmailIcons.Archive, emailFolderType);
        case EmailActionsEnum.Audit:
          return this.showIconById(EmailIcons.Seen, emailFolderType);
        case EmailActionsEnum.RetrySending:
        case EmailActionsEnum.MoveToDraft:
          return this.showIconById(EmailIcons.RetrySending, emailFolderType);
        case EmailActionsEnum.RestoreArchive:
          return this.showIconById(
            EmailIcons.RestoreFromArchive,
            emailFolderType
          );
        case EmailActionsEnum.Meta:
          return this.showMetaButton(
            email,
            selectedMailFolderId,
            mailAccFolderType
          );
        case EmailActionsEnum.Archiving:
          return (
            !!mailAccFolderType &&
            this.showArchivingButton(email, mailAccFolderType)
          );
        default:
          return false;
      }
    });
  }

  public showIconById(
    iconType: EmailIcons,
    emailFolderType?: MailFolderType
  ): boolean {
    return (
      !!emailFolderType &&
      this.formatEmailIconsByFolderType.transform(emailFolderType, iconType)
    );
  }

  showMetaButton(
    email: EmailListItemContract,
    selectedMailFolderId: number | null,
    mailAccFolderType?: MailFolderType,
    chekFolder = true
  ): boolean {
    const folderTypes = [
      MailFolderType.Sent,
      MailFolderType.Archive,
      MailFolderType.Trash,
    ];
    const emailStates = [
      EmailState.Sent,
      EmailState.SendError,
      EmailState.ErrorSavingEml,
      EmailState.NotAccepted,
      EmailState.Accepted,
      EmailState.Delivered,
      EmailState.DeliveryError,
      EmailState.VirusDetected,
    ];
    return chekFolder
      ? !!selectedMailFolderId &&
          !!mailAccFolderType &&
          folderTypes.includes(mailAccFolderType) &&
          !!email &&
          emailStates.includes(email.state)
      : !!email && emailStates.includes(email.state);
  }

  showArchivingButton(
    email: EmailListItemContract,
    mailAccFolderType: MailFolderType
  ): boolean {
    const folderTypes = [
      MailFolderType.Draft,
      MailFolderType.Outbox,
      MailFolderType.Validation,
    ];
    const emailStates = [EmailState.Draft];
    const needStateArchive = [
      ArchiveState.Archived,
      ArchiveState.Error,
      ArchiveState.NotArchived,
    ];

    return (
      !folderTypes.includes(mailAccFolderType) &&
      !emailStates.includes(email.state) &&
      !!email?.externalArchiveState &&
      needStateArchive.includes(email.externalArchiveState)
    );
  }

  public getDate(email: EmailListItemContract): string {
    const emailFolderType = this.userWorkspaceService.userFolders.find(
      e =>
        e.mailFolderId === email.mailFolderId &&
        e.mailAccountId === email.mailAccountId
    )?.folderType;
    return emailFolderType === MailFolderType.Outbox ||
      emailFolderType === MailFolderType.Draft ||
      emailFolderType === MailFolderType.Validation
      ? email.stateDateTime
      : email.sourceFolderType === MailFolderType.Draft ||
          email.sourceFolderType === MailFolderType.Outbox
        ? email.stateDateTime
        : email.sentDateTime;
  }

  transformData(
    emails: EmailListItemContract[],
    selectedMailFolderId: number | null,
    isShowFolderColumn: boolean,
    hasSourceColumn: boolean,
    reassigned: boolean | null,
    canFinalDelete: boolean,
    mailAccFolderType?: MailFolderType,
    mailAccountId?: number
  ): TransformEmailListItemContract[] {
    return emails.map(email => {
      const emailFolderType = this.userWorkspaceService?.userFolders?.find(
        e =>
          e.mailFolderId === email.mailFolderId &&
          e.mailAccountId === mailAccountId
      )?.folderType;

      const showMeta = this.showMetaButton(
        email,
        selectedMailFolderId,
        mailAccFolderType
      );
      const hasRetrySending = this.showIconById(
        EmailIcons.RetrySending,
        emailFolderType
      );
      const emailWithError = this.isRowWithError(email);
      const enabledArchive =
        this.userWorkspaceService.infoArchiveFolder[email.mailAccountId][
          email.mailFolderId
        ];
      const externalArchiveState = enabledArchive
        ? (email?.externalArchiveState ?? ArchiveState.NotReadyToCheck)
        : email?.externalArchiveState === ArchiveState.NotReadyToCheck
          ? ArchiveState.Off
          : (email?.externalArchiveState ?? ArchiveState.NotReadyToCheck);

      return {
        ...email,
        showMeta,
        needsEmptyPlaceholder: !showMeta && hasSourceColumn,
        dataSourceColumn: hasSourceColumn
          ? 'folderType' + email.sourceFolderType
          : '',
        hasRetrySending:
          hasRetrySending && email.state === EmailState.SendError,
        hasMoveToDraft: hasRetrySending && emailWithError,
        hasMeta: this.showMetaButton(
          email,
          selectedMailFolderId,
          mailAccFolderType,
          false
        ),
        folderName: isShowFolderColumn
          ? this.folderName(email.mailFolderId, mailAccountId)
          : '',
        date: this.getDate(email),
        sendingStatus: this.getSentStatus(email),
        actionButtons: this.getActionButton(
          email,
          selectedMailFolderId,
          reassigned,
          canFinalDelete,
          mailAccFolderType,
          mailAccountId
        ),
        isRowWithError: emailWithError,
        isRowAutoReassigned: !!email?.sourceEmailId && !emailWithError,
        isStarred: !!email?.views?.find(
          el => el.viewType === ViewTypeEnum.Star
        ),
        externalArchiveState,
      };
    });
  }
}
